import { Link, useLocation } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { getLocaleFromPath, withLocalePath } from '@/lib/i18n-utils'
import { COMPANY, LINKS } from '@/lib/company'

/**
 * The register's colophon. Four ruled columns under a strong rule, with the
 * jurisdiction and legal entity as the record's closing line.
 */
export function LedgerFooter() {
  const { t } = useTranslation('ledger')
  const location = useLocation()
  const locale = getLocaleFromPath(location.pathname)
  const href = (path: string) => withLocalePath(path, locale)

  const columns: Array<{ title: string; links: Array<[string, string]> }> = [
    {
      title: t('footer.platform'),
      links: [
        [t('footer.cloud'), '/cloud'],
        [t('footer.products'), '/products'],
        [t('footer.aiServices'), '/ai'],
        [t('footer.research'), '/research'],
      ],
    },
    {
      title: t('footer.company'),
      links: [
        [t('footer.about'), '/about'],
        [t('footer.engineering'), '/what-we-do'],
        [t('footer.contact'), '/contact'],
      ],
    },
    {
      title: t('footer.resources'),
      links: [
        [t('footer.blog'), '/blog'],
        [t('footer.partners'), '/work-with-us'],
        [t('footer.trust'), '/trust'],
      ],
    },
    {
      title: t('footer.legal'),
      links: [
        [t('footer.privacy'), '/privacy'],
        [t('footer.terms'), '/terms'],
      ],
    },
  ]

  return (
    <footer className="ledger-ground mt-0 border-t border-[var(--rule)]">
      <div className="ledger-wrap py-12">
        <div className="grid gap-10 md:grid-cols-12">
          <div className="md:col-span-4">
            <p className="ledger-masthead-brand text-[1.05rem] font-extrabold tracking-tight">
              {t('meta.brand')}
            </p>
            <p className="ledger-muted mt-4 max-w-sm text-[0.9rem] leading-relaxed">
              {t('footer.description')}
            </p>
            <div className="mt-5 flex flex-wrap gap-4">
              <a className="ledger-link" href={LINKS.github}>
                GitHub
              </a>
              <a className="ledger-link" href={LINKS.linkedin}>
                LinkedIn
              </a>
              <a className="ledger-link" href={LINKS.huggingface}>Hugging Face</a>
              <a className="ledger-link" href="/agents/site-guide/SKILL.md">
                {t('footer.agentGuide')}
              </a>
              <a className="ledger-link" href={LINKS.rss}>
                RSS
              </a>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8 md:col-span-8 md:grid-cols-4">
            {columns.map((col) => (
              <div key={col.title}>
                <h3 className="ledger-kicker mb-3">{col.title}</h3>
                <ul className="space-y-2">
                  {col.links.map(([name, path]) => (
                    <li key={`${col.title}-${name}`}>
                      <Link
                        to={href(path)}
                        className="ledger-focus text-[0.9rem] text-[var(--ink-2)] transition-colors hover:text-[var(--stamp)]"
                      >
                        {name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 border-t border-[var(--rule-strong)] pt-6">
          <div className="flex flex-col gap-3 text-[0.8125rem] md:flex-row md:items-center md:justify-between">
            <p className="ledger-figure text-[var(--ink-3)]">
              {COMPANY.legalName} · {COMPANY.street}, {COMPANY.postalCode}{' '}
              {COMPANY.locality}, {COMPANY.countryCode}
            </p>
            <p className="ledger-muted">
              © {new Date().getFullYear()} {t('meta.brand')}.
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
}
